import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { TagEntry } from '@/features/gate/commands'
import { useActor } from '@/auth/pin/phoneSession'
import type { MessageKey } from '@/i18n'
import { enqueueCommand } from '@/offline'
import { useToast } from '@/ui'
import { Btn, Cta, Footer, ItemRow, Row, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { plain, shortCode, shortLot, tr } from '@/features/gate/v1/text'
import type { PutAwayPayload } from '../commands'
import '../commands'
import {
  declarationInfo,
  placePath,
  type BagView,
  type SampleView,
  type StoreState,
} from '../projection'
import { checkPlace, checkPlaceBag, checkPlaceSample, classifyScan, lotCountHere } from '../rules'
import { useRefusalText } from '../components/useRefusalText'
import { useDraftState, useNow } from '../useStore'
import { DoneScreen, Hint, holdNote, IconBox, Saved, StoreHeader, Tally, WithStore } from './bits'
import { dockBags } from './logic'
import { SIcon } from './icons'
import { PlacePicker } from './PlacePicker'
import { TagScanner } from './TagScanner'

type Mode = 'PUT_AWAY' | 'MOVE'

interface PlaceDraft {
  location_id: string | null
  typed_code: string | null
  bags: Array<{ container_id: string; scanned_at: string }>
  samples: Array<{ sample_id: string; scanned_at: string }>
}

/** The words that differ between put away and move. */
const WORDS: Record<
  Mode,
  Record<
    | 'title'
    | 'scanPlace'
    | 'placeFirst'
    | 'confirm'
    | 'onList'
    | 'doneTitle'
    | 'doneLine'
    | 'doneChip',
    MessageKey
  >
> = {
  PUT_AWAY: {
    title: 'store.v1.put.title',
    scanPlace: 'store.v1.put.scanPlace',
    placeFirst: 'store.v1.put.placeFirst',
    confirm: 'store.v1.put.confirm',
    onList: 'store.v1.put.placed',
    doneTitle: 'store.v1.put.doneTitle',
    doneLine: 'store.v1.put.doneLine',
    doneChip: 'store.v1.put.doneChip',
  },
  MOVE: {
    title: 'store.v1.move.title',
    scanPlace: 'store.v1.move.scanPlace',
    placeFirst: 'store.v1.move.placeFirst',
    confirm: 'store.v1.move.confirm',
    onList: 'store.v1.move.onList',
    doneTitle: 'store.v1.move.doneTitle',
    doneLine: 'store.v1.move.doneLine',
    doneChip: 'store.v1.move.doneChip',
  },
}

/** V1 — बोरियाँ जगह पर रखें (F5.3): bags unloaded at the dock, to a bay or spot. */
export function V1PutAway() {
  const navigate = useNavigate()
  const home = () => navigate('/m/store')
  return (
    <WithStore onBack={home}>
      {(state) => (
        <PlaceFlow
          key="PUT_AWAY"
          mode="PUT_AWAY"
          state={state}
          initialLocationId={null}
          onHome={home}
        />
      )}
    </WithStore>
  )
}

/** V1 — बोरियाँ दूसरी जगह रखें (F5.9): bags already in the godown, to a new bay or spot. */
export function V1Move() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const home = () => navigate('/m/store')
  return (
    <WithStore onBack={home}>
      {(state) => (
        <PlaceFlow
          key="MOVE"
          mode="MOVE"
          state={state}
          initialLocationId={params.get('to')}
          onHome={home}
        />
      )}
    </WithStore>
  )
}

/**
 * Put away and move share this screen, as the full build's PlaceScanFlow does: scan the place label
 * (bay or spot), then each bag (and, at the sample store, each seal); the phone refuses what the
 * spec refuses and shows each lot's count at the place. One put_away / move_bags command on रख दीं.
 */
function PlaceFlow({
  mode,
  state,
  initialLocationId,
  onHome,
}: {
  mode: Mode
  state: StoreState
  initialLocationId: string | null
  onHome: () => void
}) {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const now = useNow()
  const refusal = useRefusalText()
  const words = WORDS[mode]
  const [draft, update, loaded, clear] = useDraftState<PlaceDraft>(
    `${mode === 'MOVE' ? 'store.v1.move' : 'store.v1.putaway'}:${state.siteId}`,
    mode === 'MOVE' ? 'store.v1.move' : 'store.v1.putaway',
    () => ({ location_id: initialLocationId, typed_code: null, bags: [], samples: [] }),
    actor.user_id,
  )
  const [saved, setSaved] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<{ count: number; place: string } | null>(null)

  const location = draft.location_id ? (state.locations.get(draft.location_id) ?? null) : null
  const listed = useMemo(
    () => draft.bags.map((b) => state.bags.get(b.container_id)).filter((b): b is BagView => !!b),
    [draft.bags, state.bags],
  )
  const listedSamples = draft.samples
    .map((s) => state.samples.get(s.sample_id))
    .filter((s): s is SampleView => !!s)
  const count = listed.length + listedSamples.length

  // Bags waiting on the dock: STORED with their place at the site itself (not yet in a bay).
  const dock = useMemo(() => (mode === 'PUT_AWAY' ? dockBags(state) : []), [mode, state])
  const placeCodes = useMemo(() => [...state.locationByCode.keys()], [state.locationByCode])
  const itemPayloads = useMemo(
    () => [...state.bagByPayload.keys(), ...state.sampleByPayload.keys()],
    [state.bagByPayload, state.sampleByPayload],
  )

  const onPlace = (code: string, entry: TagEntry): string | null => {
    const k = classifyScan(state, code)
    if (k.kind !== 'LOCATION') return refusal('TAG_WRONG_KIND')
    const problem = checkPlace(state, k.location)
    if (problem) return refusal(problem)
    update((d) => ({
      ...d,
      location_id: k.location.id,
      typed_code: entry === 'TYPED' ? k.location.code : null,
    }))
    return null
  }

  const onItem = (code: string): string | null => {
    if (!location) return refusal('LEVEL_NOT_ALLOWED')
    const k = classifyScan(state, code)
    const at = new Date().toISOString()
    if (k.kind === 'BAG') {
      const r = checkPlaceBag(state, k.bag, { mode, location, now, onList: listed })
      if (r === 'DUPLICATE') return null
      if (r) return refusal(r)
      update((d) => ({
        ...d,
        bags: [...d.bags, { container_id: k.bag.container_id, scanned_at: at }],
      }))
      setSaved(code)
      return null
    }
    if (k.kind === 'SEAL') {
      const r = checkPlaceSample(state, k.sample, location, listedSamples)
      if (r === 'DUPLICATE') return null
      if (r) return refusal(r)
      update((d) => ({
        ...d,
        samples: [...d.samples, { sample_id: k.sample.sample_id, scanned_at: at }],
      }))
      setSaved(code)
      return null
    }
    if (k.kind === 'DEAD') return refusal(k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED')
    if (k.kind === 'LOCATION') {
      // The place's own label, still in front of the camera: nothing to say.
      if (k.location.id === location.id) return null
      return refusal('TAG_WRONG_KIND')
    }
    return refusal('TAG_UNKNOWN')
  }

  const removeLast = () => {
    setSaved(null)
    update((d) =>
      d.samples.length > 0
        ? { ...d, samples: d.samples.slice(0, -1) }
        : { ...d, bags: d.bags.slice(0, -1) },
    )
  }

  const changePlace = () => update((d) => ({ ...d, location_id: null, typed_code: null }))

  const confirm = async () => {
    if (!location || count === 0) return
    setSaving(true)
    try {
      const payload: PutAwayPayload = {
        location_id: location.id,
        typed_code: draft.typed_code,
        items: [
          ...draft.bags.map((b) => ({
            container_id: b.container_id,
            qr_payload: state.bags.get(b.container_id)!.qr_payload,
            scanned_at: b.scanned_at,
          })),
          ...draft.samples.map((s) => ({
            sample_id: s.sample_id,
            qr_payload: state.samples.get(s.sample_id)!.seal_payload,
            scanned_at: s.scanned_at,
          })),
        ],
      }
      const parents = [
        ...new Set(
          [
            ...listed.map((b) => b.pending_command_id),
            ...listedSamples.map((s) => s.pending_command_id),
          ].filter((x): x is string => !!x),
        ),
      ]
      await enqueueCommand({
        type: mode === 'MOVE' ? 'move_bags' : 'put_away',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      setDone({ count: payload.items.length, place: location.name ?? location.code })
      await clear().catch(() => undefined)
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <DoneScreen
        title={tr(words.doneTitle)}
        lines={plain(tr(words.doneLine, { count: done.count, place: done.place }))}
        chips={[{ icon: 'godown', text: tr(words.doneChip) }]}
        onHome={onHome}
      />
    )
  if (!loaded) return <V1Screen />

  // Step 1: which place?
  if (!location)
    return (
      <V1Screen
        header={<StoreHeader onBack={onHome} icon="rowbags" title={tr(words.title)} step={1} />}
        footer={
          <Footer>
            <WaitCta>{tr(words.scanPlace)}</WaitCta>
          </Footer>
        }
      >
        <Hint icon="tag" text={tr(words.placeFirst)} />
        {dock.length > 0 && (
          <Hint icon="sack" tone="warn" text={tr('store.v1.put.onDock', { count: dock.length })} />
        )}
        <TagScanner
          hint={tr(words.scanPlace)}
          payloads={placeCodes}
          onCode={onPlace}
          kind="place"
        />
        {/* Picked from the list, not scanned: recorded as typed. */}
        <PlacePicker
          state={state}
          onPick={(id) => {
            const l = state.locations.get(id)
            if (l) onPlace(l.code, 'TYPED')
          }}
        />
      </V1Screen>
    )

  // Step 2: the bags going there.
  const decl = declarationInfo(state, location.id, now)
  const openRole =
    location.role === 'CONVENTIONAL' ||
    location.role === 'SAMPLE_STORE' ||
    location.role === 'HOLD_AREA'
  const lotIds = [...new Set(listed.map((b) => b.unit_id))]
  return (
    <V1Screen
      header={<StoreHeader onBack={onHome} icon="rowbags" title={tr(words.title)} step={2} />}
      footer={
        <Footer>
          {count === 0 && (
            <Row>
              <Btn icon="back" onClick={changePlace}>
                {tr('store.v1.put.change')}
              </Btn>
            </Row>
          )}
          {count > 0 ? (
            <Cta label={tr(words.confirm)} icon="ok" busy={saving} onClick={() => void confirm()} />
          ) : (
            <WaitCta>{tr('store.v1.scanOff.first')}</WaitCta>
          )}
        </Footer>
      }
    >
      {saved && <Saved text={tr('store.v1.tag', { tag: shortCode(saved) })} />}
      <ItemRow
        lead={<IconBox icon="godown" size={44} iconSize={26} />}
        title={location.name ?? location.code}
        line={placePath(state, location.id)}
        right={<Tally value={String(count)} label={tr(words.onList)} />}
      />
      {decl.status !== 'CLEAN' && !openRole && (
        <>
          <Hint icon="bang" tone="warn" text={tr('store.v1.put.notDeclared')} />
          <Row>
            <Btn icon="pencil" onClick={() => navigate('/m/store/declaration')}>
              {tr('store.v1.home.form')}
            </Btn>
          </Row>
        </>
      )}
      <TagScanner
        hint={tr(count ? 'store.v1.scanOn.hold' : 'store.v1.scanOff.first')}
        payloads={itemPayloads}
        onCode={onItem}
        side={
          count > 0
            ? { icon: 'undo', label: tr('store.v1.put.removeLast'), onClick: removeLast }
            : undefined
        }
      />
      {lotIds.map((u) => {
        const lot = state.lots.get(u)
        const red = lot?.stream === 'CONV'
        const c = lotCountHere(state, u, location.id, listed)
        return (
          <ItemRow
            key={u}
            padding="6px 10px"
            lead={
              <span style={{ color: red ? C.ink : C.ok, display: 'flex' }}>
                <SIcon name={red ? 'redtie' : 'sack'} size={26} />
              </span>
            }
            title={tr('store.v1.put.band', {
              grade: tr(red ? 'store.v1.band.red' : 'store.v1.band.good'),
              lot: lot ? shortLot(lot.code) : '—',
              n: c.here,
              m: c.total,
            })}
            line={holdNote(lot)}
            right={c.here === c.total ? <SIcon name="ok" size={22} sw={3} /> : undefined}
          />
        )
      })}
      {/* A move shows where each bag comes from. */}
      {mode === 'MOVE' &&
        [...listed]
          .reverse()
          .map((b) => (
            <ItemRow
              key={b.container_id}
              padding="6px 10px"
              lead={<SIcon name="tag" size={24} />}
              title={tr('store.v1.tag', { tag: shortCode(b.qr_payload) })}
              line={
                b.location_id
                  ? tr('store.v1.move.from', { place: placePath(state, b.location_id) })
                  : undefined
              }
            />
          ))}
      {listedSamples.length > 0 && (
        <ItemRow
          padding="6px 10px"
          lead={<SIcon name="seal" size={26} />}
          title={tr('store.v1.put.samples', { count: listedSamples.length })}
        />
      )}
      {mode === 'MOVE' && (
        <Row>
          <Btn icon="bang" onClick={() => navigate('/m/store/replace')}>
            {tr('store.v1.move.replace')}
          </Btn>
        </Row>
      )}
    </V1Screen>
  )
}
